import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewEncapsulation, ChangeDetectorRef } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";
import { CommonService } from "src/app/shared/services/common.service";

@Component({
    selector: "app-payment-input",
    templateUrl: "./payment-input.component.html",
    styleUrls: ["./payment-input.component.scss"],
    encapsulation: ViewEncapsulation.ShadowDom,
})
export class PaymentInputComponent implements OnDestroy, OnInit {
    @Input() invoiceData: any = null;
    @Input() selectedVehicle: any = null;

    @Output() closePaymentInputEvent = new EventEmitter<void>();
    @Output() proceedToPaymentEvent = new EventEmitter<any>();

    paymentForm: FormGroup;
    isFullPayment = true;
    private destroy$: Subject<void> = new Subject<void>();

    constructor(
        private fb: FormBuilder,
        private commonService: CommonService,
        private cdr: ChangeDetectorRef
    ) {
        this.paymentForm = this.fb.group({
            paymentType: ['full', Validators.required],
            amount: [null, [Validators.required, Validators.min(0)]]
        });

        // Subscribe to payment type changes to update amount validation
        this.paymentForm.get('paymentType')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(type => {
                const amountControl = this.paymentForm.get('amount');
                if (type === 'full') {
                    const fullAmount = this.invoiceData?.outstandingAmount || 0;
                    amountControl?.setValue(fullAmount);
                    amountControl?.disable();
                    amountControl?.setValidators([Validators.required, Validators.min(0)]);
                    this.isFullPayment = true;
                } else {
                    amountControl?.enable();
                    amountControl?.setValue(null);
                    amountControl?.setValidators([
                        Validators.required,
                        Validators.min(0),
                        Validators.max(this.invoiceData?.outstandingAmount)
                    ]);
                    this.isFullPayment = false;
                }
                amountControl?.updateValueAndValidity();
                this.cdr.detectChanges();
            });

        // Subscribe to amount changes
        this.paymentForm.get('amount')?.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(amount => {
                this.cdr.detectChanges();
            });
    }

    ngOnInit() {
        // Initialize with full payment amount if available
        if (this.invoiceData?.outstandingAmount) {
            const initialAmount = this.invoiceData.outstandingAmount;
            this.paymentForm.patchValue({
                amount: initialAmount
            });
            // Disable amount input initially since default is full payment
            this.paymentForm.get('amount')?.disable();
            this.isFullPayment = true;
            this.cdr.detectChanges();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closePaymentInput(): void {
        this.closePaymentInputEvent.emit();
    }

    onSubmit(): void {
        if (this.paymentForm.valid) {
            const formData = {
                type: this.paymentForm.get('paymentType')?.value,
                amount: this.paymentForm.get('amount')?.value
            };
            this.proceedToPaymentEvent.emit(formData);
        }
    }

    getMaxPaymentAmount(): number {
        const maxAmount = this.invoiceData?.outstandingAmount || 0.00;
        return maxAmount;
    }

    getRemainingBalance(): number {
        if (this.isFullPayment) {
            return 0.00;
        }
        const outstandingAmount = this.invoiceData?.outstandingAmount || 0.00;
        const paymentAmount = this.paymentForm.get('amount')?.value || 0.00;
        const remainingBalance = outstandingAmount - paymentAmount;
        return remainingBalance;
    }

    formatAmount(amount: number): string {
        return this.commonService.formatAmount(amount, this.selectedVehicle);
    }
} 